import React, { useState, useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import WordDefinitionModal from "./WordDefinitionModal";

// Define base definitions
const baseDefinitions: Record<
  string,
  {
    definition: string;
    imageUrl?: string;
  }
> = {
  vegur: {
    definition:
      "Mannvirki ætlað fyrir umferð. Dæmi um veg eru götur, stígar, bílastæði, brýr, hringtorg og neðanjarðargöng.",
  },
  akbraut: {
    definition:
      "Sá hluti vegar sem ætlaður er vélknúnum eða óvélknúnum ökutækjum, s.s. bílum og reiðhjólum.",
  },
  akrein: {
    definition:
      "Hluti akbrautar þar sem einföld röð ökutækja ekur í eina átt. Á akbraut geta verið fleiri en ein akrein.",
  },
  vegamót: {
    definition:
      "Staður þar sem tveir eða fleiri vegir mætast eða skerast eða þar sem einn vegur greinist í tvo eða fleiri.",
  },
  vegöxl: {
    definition:
      "Ysti hluti vegarins með bundnu slitlagi. Ætlað til að bílar geti vikið undan hraðari umferð og stöðvað í neyðartilfellum.",
  },
  beygjurein: {
    definition:
      "Akrein sem ökumaður notar til að beygja til vinstri gegnum umferð á móti.",
  },
  "bundið slitlag": {
    definition:
      "Slitlag er efsta lag vegar. Bundin slitlög eru t.d. slitlög úr steinsteypu og malbiki.",
  },
  slitlagsbrún: {
    definition:
      "Við vegkantinn endar fasta slitlagið — þá erum við venjulega að tala um malbik — og möl tekur við. Sú brún kallast slitlagsbrún.",
  },
  þjóðvegur: {
    definition:
      "Aðalvegur ætlaður almennri umferð utan þéttbýlis. Þjóðvegir eru allir þeir vegir sem eru (1) ekki í einkaeigu eða (2) ekki í umsjón sveitarfélaga.",
  },
  aðrein: {
    definition: "Aðrein er akrein sem liggur í sveig inn á akbraut.",
  },
  frárein: {
    definition: "Frárein er akrein sem liggur í sveig út af akbraut.",
  },
  hemlunarvegalengd: {
    definition:
      "Vegalengd sem ökutæki fer frá því að bílstjóri stígur á bremsuna (hemlar) og þar til ökutæki stöðvast.",
  },
  hemlunarljós: {
    definition: "Bremsuljós.",
  },
  hemla: {
    definition: "Bremsa.",
  },
  vistgata: {
    definition:
      "Í vistgötum hafa gangandi vegfarendur forgang. Þar má ekki aka yfir 15 km / klst.",
  },
  heimkeyrsla: {
    definition:
      "Bílastæði og/eða vegur upp að húsi eða bílskúr. Sama og innkeyrsla og heimreið.",
  },
  hliðarvegur: {
    definition: "Vegur út frá aðalvegi, sama og afleggjari.",
  },
  einbreiður: {
    definition: "Vegur eða brú sem aðeins eitt ökutæki getur farið eftir.",
  },
  blindhæð: {
    definition: "Hæð á vegi svo há að umferð handan hennar sést ekki.",
  },
  vegsýn: {
    definition: "Sú fjarlægð sem bílstjóri sér skýrt fram undan.",
  },
  torvelda: {
    definition: "Gera erfiðara.",
  },
  "handfrjáls búnaðar": {
    definition:
      "Hvers kyns búnaður sem gerir bílstjóra kleift að svara, tala í farsíma án þess að nota hendurnar, t.d þráðlaus heyrnatól eða tengja farsímann við bílinn með Bluetooth.",
  },
  vegstika: {
    definition:
      "Gulur stafur við vegbrún með endurskins línum (glitmerki). Stikan markar vegbrún þegar hún sést illa vegna myrkurs, snjóa eða þoku.",
  },
  vegbrún: {
    definition: "Kantur vegsins til beggja hliða.",
  },
  glituð: {
    definition: "Lýsast upp í myrkri, endurskina.",
  },
  einstefnuvegur: {
    definition: "Vegur eða gata þar sem umferð liggur í eina átt.",
  },
  torfær: {
    definition: "Erfiður yfirferðar.",
  },
  einstefna: {
    definition: "Umferð liggur bara í eina átt.",
  },
};

// Map word variations to their base forms
const wordVariations: Record<string, string> = {
  akreinin: "akrein",
  akreinar: "akrein",
  akreina: "akrein",
  vegurinn: "vegur",
  vegir: "vegur",
  vega: "vegur",
  akbrautin: "akbraut",
  akbrautir: "akbraut",
  akbrauta: "akbraut",
  vegamótið: "vegamót",
  vegamót: "vegamót",
  vegamóta: "vegamót",
  vegöxlin: "vegöxl",
  vegaxlir: "vegöxl",
  vegaxla: "vegöxl",
  "bundna slitlagsins": "bundið slitlag",
  "bundnu slitlagi": "bundið slitlag",
  slitlagsbrúnina: "slitlagsbrún",
  þjóðvegir: "þjóðvegur",
  þjóðvegum: "þjóðvegur",
  þjóðvegurinn: "þjóðvegur",
  þjóðvegar: "þjóðvegur",
  þjóðvega: "þjóðvegur",
  aðreinin: "aðrein",
  aðreinar: "aðrein",
  aðreina: "aðrein",
  fráreinin: "frárein",
  fráreinar: "frárein",
  fráreina: "frárein",
  hemlunarvegalengdina: "hemlunarvegalengd",
  hemlunarvegalengdir: "hemlunarvegalengd",
  hemlunarvegalengda: "hemlunarvegalengd",
  hemlunarljósin: "hemlunarljós",
  hemlunarljós: "hemlunarljós",
  hemlunarljósa: "hemlunarljós",
  hemlar: "hemla",
  hemlaði: "hemla",
  hemlað: "hemla",
  vistgötur: "vistgata",
  vistgötu: "vistgata",
  vistgatan: "vistgata",
  heimkeyrslur: "heimkeyrsla",
  heimkeyrslu: "heimkeyrsla",
  heimkeyrsluna: "heimkeyrsla",
  hliðarvegar: "hliðarvegur",
  hliðarvegi: "hliðarvegur",
  hliðarvegurinn: "hliðarvegur",
  hliðarvegum: "hliðarvegur",
  hliðarvega: "hliðarvegur",
  einbreiðir: "einbreiður",
  einbreiða: "einbreiður",
  einbreiðurinn: "einbreiður",
  blindhæðir: "blindhæð",
  blindhæðar: "blindhæð",
  blindhæðina: "blindhæð",
  vegsýnið: "vegsýn",
  vegsýni: "vegsýn",
  vegsýnis: "vegsýn",
  torveldar: "torvelda",
  torveldaði: "torvelda",
  torveldað: "torvelda",
  "handfrjáls búnaðar": "handfrjáls búnaðar",
  "handfrjálsa búnaðurinn": "handfrjáls búnaður",
  "handfrjálsa búnaðar": "handfrjáls búnaður",
  vegstikan: "vegstika",
  vegstikur: "vegstika",
  vegstiku: "vegstika",
  vegstikum: "vegstika",
  vegbrúnar: "vegbrún",
  vegbrúnir: "vegbrún",
  vegbrúninni: "vegbrún",
  glitandi: "glituð",
  glitir: "glituð",
  glit: "glituð",
  einstefnuvegurinn: "einstefnuvegur",
  einstefnuvegar: "einstefnuvegur",
  einstefnuvegi: "einstefnuvegur",
  einstefnuvega: "einstefnuvegur",
  torfærri: "torfær",
  torfærastur: "torfær",
  einstefnu: "einstefna",
  einstefnur: "einstefna",
};

// Helper to calculate safe modal position and width
function getSafeModalPosition(rect: DOMRect): {
  x: number;
  y: number;
  width: number;
} {
  let modalMaxWidth = Math.min(window.innerWidth - 16, 300);
  let left = rect.left;
  let top = rect.bottom + window.scrollY + 10;
  // If modal would overflow right, shift it left
  if (left + modalMaxWidth + 36 > window.innerWidth - 8) {
    left = 8;
  }
  // If modal would overflow left, align to left margin
  if (left < 8) {
    left = 8;
  }
  // If modal is wider than available space, shrink it
  if (modalMaxWidth > window.innerWidth - 16) {
    modalMaxWidth = window.innerWidth - 16;
    left = 8;
  }
  // Adjust if modal would overflow bottom edge
  const modalHeight = 160; // estimate
  if (top + modalHeight > window.innerHeight + window.scrollY) {
    top = rect.top + window.scrollY - modalHeight - 10;
  }
  return { x: left, y: top, width: modalMaxWidth };
}

const StepContent = ({ stepData }: { stepData: string | null }) => {
  const [hoveredWord, setHoveredWord] = useState<string | null>(null);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [modalWidth, setModalWidth] = useState<number>(300); // default max width
  const contentRef = useRef<HTMLDivElement>(null);

  const processedStepData = processStepData(stepData);
  const cleanStepData = purifyString(processedStepData);

  const handleOpenModal = (e: React.MouseEvent) => {
    if (!(e.target instanceof HTMLElement)) return;
    if (e.target.classList.contains("highlighted-word")) {
      const word = e.target.textContent?.toLowerCase() || "";
      const baseWord = wordVariations[word] || word;
      if (baseDefinitions[baseWord]) {
        const rect = e.target.getBoundingClientRect();
        const { x, y, width } = getSafeModalPosition(rect);
        setModalWidth(width);
        setModalPosition({ x, y });
        setHoveredWord(baseWord);
        e.preventDefault();
        e.stopPropagation();
      }
    } else if (!e.target.closest(".word-definition-modal")) {
      setHoveredWord(null);
    }
  };

  useEffect(() => {
    const handleMouseOver = (event: MouseEvent) => {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target.classList.contains("highlighted-word")
      ) {
        const word = target.textContent?.toLowerCase();
        if (word) {
          const baseWord = wordVariations[word] || word;
          if (baseDefinitions[baseWord]) {
            const rect = target.getBoundingClientRect();
            const { x, y, width } = getSafeModalPosition(rect);
            setModalWidth(width);
            setModalPosition({ x, y });
            setHoveredWord(baseWord);
          }
        }
      }
    };

    const handleMouseOut = (event: MouseEvent) => {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target.classList.contains("highlighted-word")
      ) {
        setHoveredWord(null);
      }
    };

    // Add mouseover/mouseout event listeners
    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener("mouseover", handleMouseOver);
      contentElement.addEventListener("mouseout", handleMouseOut);
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener("mouseover", handleMouseOver);
        contentElement.removeEventListener("mouseout", handleMouseOut);
      }
    };
  }, []);

  // Add a document-level click handler that closes the modal when clicking outside
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      if (!(e.target instanceof HTMLElement)) return;

      if (
        hoveredWord && // Modal is open
        !e.target.closest(".word-definition-modal") &&
        !e.target.classList.contains("highlighted-word")
      ) {
        setHoveredWord(null);
      }
    };

    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [hoveredWord]);

  return (
    <div className="lotu-content">
      <div
        ref={contentRef}
        onClick={handleOpenModal}
        dangerouslySetInnerHTML={{ __html: cleanStepData }}
      ></div>
      {hoveredWord && baseDefinitions[hoveredWord] && (
        <WordDefinitionModal
          word={hoveredWord}
          definition={baseDefinitions[hoveredWord].definition}
          imageUrl={baseDefinitions[hoveredWord].imageUrl}
          position={modalPosition}
          width={modalWidth}
        />
      )}
    </div>
  );
};

const processStepData = (string: string | null) => {
  if (string === null) return null;

  // Create a map to track which base words have been found
  const foundBaseWords = new Set<string>();
  let processedString = string;

  // Create a combined map of all words and their base forms
  const allWords = new Map<string, string>();

  // Add base words
  Object.keys(baseDefinitions).forEach((word) => {
    allWords.set(word.toLowerCase(), word);
  });

  // Add variations
  Object.entries(wordVariations).forEach(([variation, baseWord]) => {
    allWords.set(variation.toLowerCase(), baseWord);
  });

  // Find all word matches in order of appearance
  const matches: Array<{ word: string; baseWord: string; index: number }> = [];
  allWords.forEach((baseWord, word) => {
    // Use a custom word boundary pattern that includes Icelandic characters
    const regex = new RegExp(
      `(?<![a-zA-ZáéíóúýþæöðÁÉÍÓÚÝÞÆÖÐ])${word}(?![a-zA-ZáéíóúýþæöðÁÉÍÓÚÝÞÆÖÐ])`,
      "gi"
    );
    let match;
    while ((match = regex.exec(processedString)) !== null) {
      matches.push({
        word: match[0],
        baseWord,
        index: match.index,
      });
    }
  });

  // Sort matches by their position in the text
  matches.sort((a, b) => a.index - b.index);

  // Process matches in order, skipping if base word was already found
  let offset = 0;
  matches.forEach(({ word, baseWord, index }) => {
    if (foundBaseWords.has(baseWord)) return;

    const start = index + offset;
    const end = start + word.length;
    const before = processedString.slice(0, start);
    const after = processedString.slice(end);
    const highlighted = `<span class="highlighted-word" role="button" tabindex="0">${word}</span>`;

    processedString = before + highlighted + after;
    offset += highlighted.length - word.length;
    foundBaseWords.add(baseWord);
  });

  return processedString;
};

const purifyString = (string: string | null) => {
  if (string === null) return "";
  return DOMPurify.sanitize(string, {
    ADD_TAGS: ["iframe", "span"],
    ADD_ATTR: [
      "allow",
      "allowfullscreen",
      "frameborder",
      "scrolling",
      "class",
      "role",
      "tabindex",
    ],
  });
};

export default StepContent;
